import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { PlusIcon } from '@heroicons/react/24/outline';
import BankrollTransactionForm from './BankrollTransactionForm';
import { useBankrollMismatch } from '../contexts/BankrollMismatchContext';
import { formatHours } from '../utils/formatting';

// Helper function to format numbers with commas
const formatNumber = (num) => {
    return Math.round(parseFloat(num) || 0).toLocaleString();
};

function BalanceCard({ balance, stats, onUpdate, className = '' }) {
    const [openDeposit, setOpenDeposit] = useState(false);
    const { triggerMismatchCheck } = useBankrollMismatch();

    const handleDepositSuccess = () => {
        setOpenDeposit(false);
        if (onUpdate) onUpdate();
        triggerMismatchCheck(); // Trigger mismatch check after deposit
    };

    return (
        <>
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 
                          rounded-lg p-4 sm:p-6 shadow-sm relative h-full 
                          flex flex-col ${className}`}>
                <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Summary</h2>
                    <button
                        onClick={() => setOpenDeposit(true)}
                        className="p-1 sm:p-2 text-gray-600 hover:text-gray-900 
                                 dark:text-gray-400 dark:hover:text-white
                                 transition-colors duration-200"
                    >
                        <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                </div>

                <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 text-center">
                    ${formatNumber(balance)}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 text-center">
                    Bankroll
                </div>

                {/* YTD, MTD, WTD Profit Metrics */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="text-center">
                        <div className={`text-sm sm:text-base font-bold ${(stats?.YTD?.total_profit || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${formatNumber(stats?.YTD?.total_profit)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">YTD Profit</div>
                    </div>
                    <div className="text-center">
                        <div className={`text-sm sm:text-base font-bold ${(stats?.MTD?.total_profit || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${formatNumber(stats?.MTD?.total_profit)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">MTD Profit</div>
                    </div>
                    <div className="text-center">
                        <div className={`text-sm sm:text-base font-bold ${(stats?.WTD?.total_profit || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${formatNumber(stats?.WTD?.total_profit)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">WTD Profit</div>
                    </div>
                </div>

                {/* YTD, MTD, WTD Profit Metrics (Including Rakeback) */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className="text-center">
                        <div className={`text-sm sm:text-base font-bold ${(stats?.YTD?.total_profit_with_rakeback || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${formatNumber(stats?.YTD?.total_profit_with_rakeback || 0)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">YTD + Rakeback</div>
                    </div>
                    <div className="text-center">
                        <div className={`text-sm sm:text-base font-bold ${(stats?.MTD?.total_profit_with_rakeback || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${formatNumber(stats?.MTD?.total_profit_with_rakeback || 0)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">MTD + Rakeback</div>
                    </div>
                    <div className="text-center">
                        <div className={`text-sm sm:text-base font-bold ${(stats?.WTD?.total_profit_with_rakeback || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${formatNumber(stats?.WTD?.total_profit_with_rakeback || 0)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">WTD + Rakeback</div>
                    </div>
                </div>

                <div className="space-y-2 sm:space-y-3">
                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">LTD</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                            ${formatNumber(stats?.LTD?.total_profit)}
                        </span>
                    </div>
                </div>

                <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Deposits</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                            ${formatNumber(stats?.LTD?.total_deposits)}
                        </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Withdrawals</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                            ${formatNumber(stats?.LTD?.total_withdrawals)}
                        </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Rakeback</span>
                        <span className="font-medium text-green-600 dark:text-green-400">
                            ${formatNumber(stats?.LTD?.total_rakeback)}
                        </span>
                    </div>
                </div>

                <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Total Sessions</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                            {stats?.LTD?.total_sessions || 0}
                        </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Hourly Rate</span>
                        <span className={`font-medium ${(stats?.LTD?.total_profit || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${(() => {
                                const totalProfit = parseFloat(stats?.LTD?.total_profit) || 0;
                                const totalHours = parseFloat(stats?.LTD?.total_hours) || 0;
                                if (totalHours === 0) return '0';
                                return (totalProfit / totalHours).toFixed(2);
                            })()}
                        </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Hourly Rate + Rakeback</span>
                        <span className={`font-medium ${(stats?.LTD?.total_profit_with_rakeback || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            ${(() => {
                                const totalProfit = parseFloat(stats?.LTD?.total_profit_with_rakeback) || 0;
                                const totalHours = parseFloat(stats?.LTD?.total_hours) || 0;
                                if (totalHours === 0) return '0';
                                return (totalProfit / totalHours).toFixed(2);
                            })()}
                        </span>
                    </div>

                    <div className="flex justify-between text-xs sm:text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Total Time</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                            {formatHours(parseFloat(stats?.LTD?.total_hours) || 0)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Deposit Modal */}
            <Transition appear show={openDeposit} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setOpenDeposit(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                        Add Transaction
                                    </Dialog.Title>
                                    <BankrollTransactionForm onSuccess={handleDepositSuccess} />
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
}

export default BalanceCard; 